import { SearchOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Button, Col, Input, Row, Space } from 'antd';
import { useEffect, useState } from 'react';
import { getShipments } from '../../api';
import { components, paths } from '../../api/schema';
import DataTable, {
  ColumnsType,
  DataTableFilterCallback,
  DataTableOrderCallback,
  serializeFilters,
  serializeOrders,
} from '../../shared/dataTable/DataTable';
import useDebounce from '../../shared/hooks/useDebounce';
import PanelSkeleton from '../../shared/skeletons/PanelSkeleton';
import CreateService from './CreateService';
import { LabelDrawer } from './pickupPanelActions/LabelDrawer';
import useConditionalRefresh from '../../shared/hooks/useConditionalRefresh';
import { ViewCfdi } from '../../shared/shipment/ViewCfdi';

const pickupsTableColumns: ColumnsType<components['schemas']['ClientShipmentCondensed']> = [
  {
    title: 'Fecha creación',
    dataIndex: 'createdAt',
    key: 'createdAt',
    columnType: 'date',
  },
  {
    title: 'Número de rastreo',
    dataIndex: 'shipmentTrackingNumber',
    key: 'shipmentTrackingNumber',
    columnType: 'text',
  },
  {
    title: 'Cliente',
    dataIndex: 'clientName',
    key: 'clientName',
    columnType: 'text',
    hideFilter: true,
  },
  {
    title: 'Peso total',
    dataIndex: 'totalWeight',
    key: 'totalWeight',
    columnType: 'text',
    hideFilter: true,
  },
  {
    title: 'Total de paquetes',
    dataIndex: 'totalPackages',
    key: 'totalPackages',
    columnType: 'text',
    hideFilter: true,
  },
  {
    title: 'Estatus',
    dataIndex: 'status',
    key: 'status',
    columnType: 'enum',
    customCellClass: (r) => {
      if (r.status === 'Esperando recolección' || r.status === 'En almacén') {
        return 'status-yellow text-center';
      } else if (r.status === 'Recolectado' || r.status === 'En tránsito') {
        return 'status-blue text-center';
      } else if (r.status === 'Entregado') {
        return 'status-green text-center';
      }
    },
  },
];
const PAGE_SIZE = 50;

export default function PickupPanel() {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [search, setSearch] = useState<string>('');
  const debouncedSearch = useDebounce<string>(search, 300);
  const [params, setParams] = useState<paths['/user-client/shipment/list']['get']['parameters']['query']>({
    search: undefined,
    limit: PAGE_SIZE,
    page: 1,
    showDisabled: false,
    order: JSON.stringify([]),
    filter: JSON.stringify([]),
  });
  const { isLoading, isError, data, isFetching, refetch } = useQuery({
    queryKey: ['getShipments', params],
    queryFn: () => getShipments(params),
  });

  useConditionalRefresh(60000, refetch);

  useEffect(() => {
    setParams((state) => ({ ...state, search: debouncedSearch }));
  }, [debouncedSearch]);

  const handleFiltering = (filter: DataTableFilterCallback) => {
    setParams((state) => ({ ...state, filter: serializeFilters(params.filter, filter) }));
  };

  const handleOrdering = (order: DataTableOrderCallback) => {
    setParams((state) => ({ ...state, order: serializeOrders(params.order, order) }));
  };

  const handlePageChange = (page: number) => {
    setParams((state) => ({ ...state, page }));
  };

  return (
    <Row>
      <Col span={24}>
        <Space size={'middle'}>
          <Button type="primary" onClick={() => setCreateModalOpen(true)}>
            Crear servicio
          </Button>
          <Input
            placeholder="Buscar..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            suffix={<SearchOutlined />}
          />
        </Space>
      </Col>
      {isLoading || isError || isFetching ? (
        <PanelSkeleton />
      ) : (
        <>
          <Col span={24} style={{ marginTop: '10px' }}>
            <DataTable<components['schemas']['ClientShipmentCondensed']>
              handleFilters={handleFiltering}
              handleOrder={handleOrdering}
              handlePageChange={handlePageChange}
              columns={pickupsTableColumns}
              results={data.results}
              activeFilters={params.filter}
              activeOrders={params.order}
              paginationData={{
                pageSize: PAGE_SIZE,
                count: data.count,
                currentPage: data.currentPage,
              }}
              actions={[LabelDrawer, ViewCfdi]}
            />
          </Col>

          <CreateService
            open={createModalOpen}
            onCancel={() => setCreateModalOpen(false)}
            onCreate={() => setCreateModalOpen(false)}
          />
        </>
      )}
    </Row>
  );
}
